import {CheckboxContext, useCheckboxSelection} from "../Checkbox";
import {
    Alert,
    Button,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Menu,
    MenuItem,
    TextField
} from "@mui/material";
import {Dialog2} from "../../../../misc/Dialog2";
import {SmoothActionBar} from "../../../../misc/SmoothActionBar";
import {useContext, useRef, useState} from "react";
import {useBackgroundAction} from "../../manage/sov/UseBackgroundTask";
import {api} from "../../../../api/API";
import {useTableData, useTableRefresher} from "../../../../misc/scroller/Table";
import {logisticsTableName, LogisticsTab} from "./Logistics";
import {useProjectId} from "../ProjectName";
import { Loader } from "../../../../misc/Loader";
import {InventoryRequest} from "../../../../api/Logistics";
import {ReleaseResult} from "../../../../api/Openings";
import {useUser} from "../../../../misc/Permission";
import {DateField} from "../../../../misc/DateField";
import {ChangesetContext} from "../changeset/ChangesetContext";
import {NonStockingAvailableModal} from "./NonStockingAvailableModal";
import {LogisticsView} from "./ViewButton";
import {distinct} from "nate-react-api-helpers";

export function canRelease(row: InventoryRequest) {
    return row.status === "not-released";
}

export function canUnrelease(row: InventoryRequest) {
    return row.status === "released" || row.status === "po-draft" || (row.isStocking && row.status === "in-warehouse");
}

function tabToCategory(tab: LogisticsTab) {
    switch(tab) {
        case "frame-hm": return "frame";
        case "door-hm":
        case "door-wd": return "door";
        default: return tab;
    }
}

export function BulkRelease(props: {
    type: LogisticsTab;
    view: LogisticsView;
    anchorEl: HTMLElement | null;
    onOpen(): void;
}) {
    const sel = useCheckboxSelection()
    const checkboxCtx = useContext(CheckboxContext);
    const changeset = useContext(ChangesetContext);
    const [open, setOpen] = useState(false);
    const [note, setNote] = useState("")
    const [neededBy, setNeededBy] = useState<string|null>(null)
    const [confirmPONumbers, setConfirmPONumbers] = useState<string|null>(null);
    const confirmUnreleasePODraft = useRef<boolean>();
    const [showIncludeRelatedAnchors, setShowIncludeRelatedAnchors] = useState(false);
    const includeRelatedAnchors = useRef<boolean>();
    const [showNonStockingAvailable, setShowNonStockingAvailable] = useState<number>();
    const project = useProjectId();
    const user = useUser();
    const authorizedBy = user?.name || "";
    const refresher = useTableRefresher(logisticsTableName(project, props.type));
    const data = useTableData(logisticsTableName(project, props.type)) as InventoryRequest[]

    const selected = data.filter(d => sel.indexOf(d.id) !== -1);
    const releasable = selected.filter(canRelease);
    const unreleasable = selected.filter(canUnrelease);

    // release view only releases; default view picks the action from the selected rows
    const mixed = props.view !== "release" && releasable.length > 0 && unreleasable.length > 0;
    const unRelease = props.view !== "release" && releasable.length === 0;
    const values = unRelease ? unreleasable : releasable;

    const update = useBackgroundAction({
        callback: async (input: Parameters<typeof api.openings.releaseTask>[0]) => {
            return api.openings.releaseTask({
                ...input,
                confirmUnreleasePODraft: confirmUnreleasePODraft.current,
                includeRelatedAnchors: includeRelatedAnchors.current,
            });
        },
        onDone: (result: ReleaseResult) => {
            if (result.confirmIncludeRelatedAnchors) {
                setShowIncludeRelatedAnchors(true);
                return;
            }

            if (result.confirmUnreleasePODraft) {
                setConfirmPONumbers(result.confirmUnreleasePODraft);
                return;
            }

            if (result.hasNonStockingAvailable) {
                setShowNonStockingAvailable(result.releaseId);
            }

            confirmUnreleasePODraft.current = undefined;
            includeRelatedAnchors.current = undefined;
            setOpen(false);
            setNote("");
            // neededBy is kept so the next release on this page defaults to the same date
            checkboxCtx.reset();
            refresher();
        },
    });

    const buildInput = (): Parameters<typeof api.openings.releaseTask>[0] => {
        const category = tabToCategory(props.type);

        // releasing a frame or door goes by opening so all of the opening's pending
        // items for that kind go together. Rows without an opening go by request.
        const byOpening = !unRelease && (category === "frame" || category === "door");
        const openings = byOpening ? distinct(values.filter(v => !!v.opening).map(v => v.opening)) : [];
        const requests = byOpening ? values.filter(v => !v.opening).map(v => v.id) : values.map(v => v.id);

        return {
            requests: requests,
            neededBy: unRelease ? null : neededBy,
            doorForOpenings: category === "door" ? openings : [],
            frameForOpenings: category === "frame" ? openings : [],
            note: note,
            type: category,
            project: project,
            unRelease: unRelease,
        }
    };

    const cancelDialogs = () => {
        confirmUnreleasePODraft.current = undefined;
        includeRelatedAnchors.current = undefined;
        setConfirmPONumbers(null);
    }

    const label = mixed ? "Mixed selection" :
        unRelease ? "UnRelease" : "Release";

    return <>
        <MenuItem disabled={mixed || values.length === 0} onClick={() => {
            props.onOpen();
            setOpen(true);
        }}>{label}</MenuItem>

        {showIncludeRelatedAnchors && <Dialog2 open>
            <DialogTitle>{unRelease ? "Unrelease" : "Release"} Related Anchors?</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {unRelease ? <>
                        There are separate anchors for these frames that are still released.
                        Would you like to also unrelease those anchors at the same time?
                    </> : <>
                        There are separate anchors for these frames that aren't released yet.
                        Would you like to also release those anchors at the same time?
                    </>}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => {
                    setShowIncludeRelatedAnchors(false);
                    includeRelatedAnchors.current = false;
                    update.callback(buildInput());
                }}>No, thank you</Button>
                <Button variant="contained" onClick={() => {
                    setShowIncludeRelatedAnchors(false);
                    includeRelatedAnchors.current = true;
                    update.callback(buildInput());
                }}>Yes, please</Button>
            </DialogActions>
        </Dialog2>}

        {confirmPONumbers && <Dialog2 open onClose={cancelDialogs}>
            <DialogTitle>Remove items from draft PO?</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    The selected logistics items are on the following draft purchase order(s): #{confirmPONumbers}<br/>
                    Unreleasing will remove these items from those purchase orders.
                    Any purchase order with no remaining items will be archived.
                </DialogContentText>
            </DialogContent>
            <SmoothActionBar
                loading={update.loading}
                error={update.error}
                onCancel={cancelDialogs}
                onSubmit={() => {
                    confirmUnreleasePODraft.current = true;
                    setConfirmPONumbers(null);
                    update.callback(buildInput());
                }}
                actionName="Yes, remove from PO"
            />
        </Dialog2>}

        {showNonStockingAvailable && <NonStockingAvailableModal releaseId={showNonStockingAvailable} tab={props.type} onClose={() => {
            setShowNonStockingAvailable(undefined);
        }} />}

        {open && <Menu open anchorEl={props.anchorEl} onClose={() => setOpen(false)}>
            <Loader {...update} />
            <div style={{padding: 16, maxWidth: 400}}>
                {unRelease ?
                    <>This will unrelease the selected items and remove them from purchasing screens.</> :
                    <>This will release the selected items to purchasing.</>}
                <div style={{height: 16}} />
                <TextField select disabled value={authorizedBy} fullWidth label="Authorized By">
                    <MenuItem value={authorizedBy}>{authorizedBy}</MenuItem>
                </TextField>
                <div style={{height: 16}} />
                <TextField onChange={e => setNote(e.target.value)} value={note} fullWidth label="Note" />
                {!unRelease && <>
                    <div style={{height: 16}} />
                    <DateField
                        label="Needed By"
                        nullable
                        fullWidth
                        size="medium"
                        format="YYYY-MM-DD"
                        initialValue={neededBy}
                        onChange={setNeededBy} />
                </>}
                {!unRelease && changeset.proposing && <Alert severity="error" style={{marginTop: 16}}>
                    You have pending changes that require approval. Please resolve these before you release items.
                </Alert>}
            </div>
            <Grid container spacing={1} justifyContent="flex-end" style={{paddingRight: 10, paddingBottom: 8}}>
                <Grid item>
                    <Button onClick={() => setOpen(false)} size="small">Cancel</Button>
                </Grid>
                <Grid item>
                    <Button
                        disabled={!unRelease && changeset.proposing}
                        onClick={() => {
                            confirmUnreleasePODraft.current = undefined;
                            includeRelatedAnchors.current = undefined;
                            update.callback(buildInput());
                        }} variant="contained" size="small">{unRelease ? "UnRelease" : "Release"}</Button>
                </Grid>
            </Grid>
        </Menu>}
    </>
}
